import React from 'react'
import { Controller, FieldError } from 'react-hook-form'
import { Error, Label } from '../styles'
import ReactSelect from 'react-select'
import makeAnimated from 'react-select/animated'
import { darken } from 'polished'
import { Content } from './styles'
import { useTheme } from 'styled-components'

export interface SelectOptions {
  value: string
  label: string
}

export interface SelectInputProps {
  placeholder?: string
  control: any
  onChangeText?: (search?: string) => void
  label?: string
  Icon?: any
  onSelect?: (item: any) => any
  menuPortalTarget?: any
  menuPosition?: any
  gray?: any
  disabled?: boolean
  isLoading?: boolean
  zIndex?: any
  error?: FieldError
  name: string
  small?: boolean
  options?: SelectOptions[]
  isClearable?: boolean
  isSearchable?: boolean
  isMulti?: boolean
}

const SelectInput: React.FunctionComponent<SelectInputProps> = ({
  control,
  placeholder = 'Selecione uma opção',
  label,
  disabled,
  onChangeText,
  onSelect,
  error,
  menuPosition = 'absolute',
  options = [],
  name,
  small,
  isLoading = false,
  menuPortalTarget,
  isClearable,
  gray,
  isSearchable,
  isMulti,
  Icon,
  zIndex,
}) => {
  const animatedComponents = makeAnimated()

  const theme: any = useTheme()

  const customStyles = {
    option: (styles: any, { data, isDisabled, isSelected }: any) => ({
      ...styles,
      color: isSelected ? 'white' : theme.colors.input,
      ':active': {
        ...styles[':active'],
        backgroundColor: !isDisabled && (isSelected ? theme.colors.primary : 'transparent'),
      },
      ':hover': {
        backgroundColor:
          !isDisabled && (isSelected ? data.color : darken(-0.6, theme.colors.primary)),
      },
      backgroundColor: !isDisabled && (isSelected ? theme.colors.primary : 'transparent'),
      cursor: isDisabled ? 'not-allowed' : 'default',
    }),
    menu: (styles: any) => ({
      ...styles,
      fontSize: 14,
      color: darken(-0.6, theme.colors.primary),
    }),
    singleValue: (styles: any) => ({
      ...styles,
      fontSize: 14,
      color: theme.colors.input,
      fontWeight: '600',
    }),
    container: (styles: any) => ({
      ...styles,
      width: '100%',
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (styles: any) => ({
      ...styles,
      height: small ? 35 : 45,
      fontSize: 14,
      backgroundColor: 'transparent',
      borderRadius: 10,
      border: 'none !important',
      boxShadow: 'none !important',
      ':hover': {
        border: 'none !important',
        boxShadow: 'none !important',
      },
    }),
    placeholder: (styles: any) => ({
      ...styles,
      color: darken(-0.6, theme.colors.placeholder),
      fontSize: 14,
    }),
    input: (styles: any) => ({
      ...styles,
      fontSize: 14,
      color: theme.colors.input,
    }),
  }

  const selectProps: any = {
    instanceId: name,
    classNamePrefix: 'react-select',
    isMulti,
    isSearchable,
    isClearable,
    options,
    placeholder,
    isLoading,
    components: animatedComponents,
    noOptionsMessage: () => 'Nenhuma opcão',
  }
  if (menuPortalTarget) {
    selectProps.menuPortalTarget = menuPortalTarget
  }

  return (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => {
        const handleChange = (option: SelectOptions) => {
          if (isMulti) {
            onChange(option)
            if (onSelect) {
              onSelect(option)
            }
          } else {
            if (option) {
              onChange(option.value)
              if (onSelect) {
                onSelect(option)
              }
            } else {
              onChange(null)
              if (onSelect) {
                onSelect(null)
              }
            }
          }
        }
        return (
          <div style={{ width: '100%', position: 'relative' }}>
            {label && <Label>{label}</Label>}

            <Content gray={gray}>
              {Icon && Icon}

              <ReactSelect
                menuPosition={menuPosition || 'absolute'}
                menuShouldBlockScroll={true}
                isDisabled={disabled}
                onChange={(value: any) => handleChange(value)}
                styles={customStyles}
                value={isMulti ? value || null : options.find((vl) => vl.value === value) || null}
                onInputChange={(search) => {
                  if (typeof onChangeText !== 'undefined') {
                    onChangeText(search)
                  }
                }}
                {...selectProps}
              />
            </Content>

            {error && <Error>{error.message}</Error>}
          </div>
        )
      }}
    />
  )
}

export default SelectInput
